import React, { useEffect } from "react";
import { makeStyles } from '@material-ui/styles';
import { useSelector, useDispatch } from 'react-redux';
import { actions } from 'store/guestInsights';
import { TableComponentV2, ToolbarV2 } from 'components';
import { GuestInsightsTableTitles, guestInfoValuesList, contactInfoValuesList, consentValuesList, dateRangeValuesList } from 'constants.js';
import filterHelpers from 'store/helpers/filterHelpers';
import FormController from 'common/formController';
import guestInsightValidator from './validator';

const { 
  getPaginatedGuestInsights,
  setCurrentGuestDetail,
  unsetCurrentGuestDetail,
  updateGuestInsightFilters,
  resetGuestInsightFilters
} = actions;

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3)
  },
  content: {
    marginTop: theme.spacing(2)
  }
}));

const GuestInsights = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const formController = new FormController();
  const {
    onClickEdit
  } = filterHelpers();
  const guestInsights = useSelector(state => state.guestInsights.paginatedList);
  const isLoading = useSelector(state => state.guestInsights.gettingPaginated);
  const filters = useSelector(
    store => store.guestInsights.filters
  );
  const [queryDataErrors, setQueryDataErrors] = React.useState({});
  const optionsToFilterGuestInfoValues = guestInfoValuesList.map(guestInfo => {
    return { name: 'guestInfoValue', checked: false, regex: '', ...guestInfo };
  });
  const optionsToFilterContactInfoValues = contactInfoValuesList.map(contactInfo => {
    return { name: 'contactInfoValue', checked: false, regex: '', ...contactInfo };
  });
  const optionsToFilterConsentValues = consentValuesList.map(consent => {
    return { name: 'consentValue', checked: false, regex: '', ...consent };
  });
  const optionsToFilterDateValues = dateRangeValuesList.map(dateRange => {
    return { name: dateRange.value, checked: false, regex: '', ...dateRange };
  });

  const validate = (data) => {
    const validation = guestInsightValidator.validate(data || dateRange, {
      abortEarly: false,
      allowUnknown: true // allow unknown keys that will be ignored
    });
    let errors = formController.getFormErrors(validation, {
      saveAttempted: true
    });
    setQueryDataErrors(errors);
    return errors;
  };

  const getTransformedGuestInsights = row => {
    let transformedGuestInsights = row.map(row => {
      return {
        ...row,
        ...row.guestInformation
      }
    });
    return transformedGuestInsights;
  };

  const getGuestInfoList = () => {
    return filters?.filterGuestInfoValues?.filter(option => option.checked).map(option => option.value);
  };

  const getcontactInfoList = () => {
    return filters?.filterContactInfoValues?.filter(option => option.checked).map(option => option.value);
  };

  const getconsentList = () => {
    return filters?.filterConsentValues?.filter(option => option.checked).map(option => option.value);
  };

  const getGuestInfoMap = () => {
    return filters?.filterGuestInfoValues?.reduce((acc, option) => {
      acc[option.value] = option.regex;
      return acc;
    }, {}) || {};
  };

  const getContactInfoMap = () => {
    return filters?.filterContactInfoValues?.reduce((acc, option) => {
      acc[option.value] = option.regex;
      return acc;
    }, {}) || {};
  };
  const getConsentMap = () => {
    return filters?.filterConsentValues?.reduce((acc, option) => {
      acc[option.value] = option.regex;
      return acc;
    }, {}) || {};
  };

  const getActionButtons = template => [
    {
      name: 'View Details',
      action: ['ACTION', () => onEdit(template)]
    }
  ];

  const onEdit = row => {
    onClickEdit(
      [unsetCurrentGuestDetail(), setCurrentGuestDetail(row)],
      `/guest-insights/details/${row.guestProfileUuid}`
    );
  };

  useEffect(() => {
    dispatch(getPaginatedGuestInsights({ force: true }));
  }, []);

  const shouldShowError = name => {
    return formController.shouldShowError(queryDataErrors, name);
  };

  const getErrorMessage = name => {
    return formController.getErrorMessage(queryDataErrors, name);
  };

  const filterOptions = {
    validation: {
      shouldShowError,
      getErrorMessage,
      validate
    },
    regexFilters: [{
      options: optionsToFilterGuestInfoValues.map(option => {
        if (getGuestInfoList()?.includes(option.value)) {
          option.checked = true;
          option.regex = getGuestInfoMap()[option.value] || '';
        }
        return option;
      }) || [],
      title: 'Guest Info',
      onChange: guestInfoValues => dispatch(updateGuestInsightFilters({ filterGuestInfoValues: guestInfoValues, pageNo: 0 })),
      intraDependency: filters?.filterGuestInfoValues
    },{
      options: optionsToFilterContactInfoValues.map(option => {
        if (getcontactInfoList()?.includes(option.value)) {
          option.checked = true;
          option.regex = getContactInfoMap()[option.value] || '';
        }
        return option;
      }) || [],
      title: 'Contact Info',
      onChange: contactInfoValues => dispatch(updateGuestInsightFilters({ filterContactInfoValues: contactInfoValues, pageNo: 0 })),
      intraDependency: filters?.filterContactInfoValues
    },{
      options: optionsToFilterConsentValues.map(option => {
        if (getconsentList()?.includes(option.value)) {
          option.checked = true;
          option.regex = getConsentMap()[option.value] || '';
        }
        return option;
      }) || [],
      title: 'Consent',
      onChange: consentValues => dispatch(updateGuestInsightFilters({ filterConsentValues: consentValues, pageNo: 0 })),
      intraDependency: filters?.filterConsentValues
    },{
      options: optionsToFilterDateValues.map(option => {
        const dateValue = filters?.filterDateValues?.find(o => o.value === option.value);
        if (dateValue) {
          option.checked = dateValue.checked;
          option.regex = dateValue.regex || { start: '', end: '' };
        }
        return option;
      }) || [],
      title: 'Date',
      backdropBlur: true,
      enableMonthYearPicker: true,
      onChange: (dateValues) => dispatch(updateGuestInsightFilters({ filterDateValues: dateValues, pageNo: 0 })),
      intraDependency: filters?.filterDateValues
    }],
    resetFilters: () => dispatch(resetGuestInsightFilters())
  }
  return (
    <div className={classes.root}>
      <ToolbarV2
        buttonsGroup={[]}
        action={getPaginatedGuestInsights({ force: true })}
        filterOptions={filterOptions}
        showOnlyFilters
      />
      <div className={classes.content}>
        <TableComponentV2
          isLoading={isLoading}
          tableTitles={GuestInsightsTableTitles}
          onDoubleClickRow={row => onEdit(row)}
          actionButtonOptions={row => getActionButtons(row)}
          filteredRows={getTransformedGuestInsights(guestInsights)}
          action={getPaginatedGuestInsights({ force: true })}
          filterValues={filters}
          filterAction={(payload) => dispatch(updateGuestInsightFilters(payload))}
        />
      </div>
    </div>
  );
};

export default GuestInsights;